/**
 * Tiny helper for authenticated fetches that don't fit `apiRequest`'s
 * JSON-only contract — namely `FormData` uploads and bodyless POSTs
 * triggered from non-React contexts (e.g. the screen-snapshot capture
 * loop in `call-context`).
 *
 * Auth now rides on httpOnly cookies (set by the login/refresh
 * endpoints); the server's `authenticate` middleware reads the
 * `accessToken` cookie, so all this helper needs to do is ensure
 * credentials are included.
 */
/**
 * Read the double-submit CSRF token set by the server at login/refresh.
 * The cookie is deliberately non-httpOnly so same-origin JS can echo it
 * back as the X-CSRF-Token header on state-changing requests.
 */
export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null; // SSR/node test environments
  const match = document.cookie.match(/(?:^|;\s*)csrf-token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Add X-CSRF-Token to headers for state-changing methods (mutates nothing). */
export function withCsrfHeader(method: string | undefined, headers: HeadersInit = {}): HeadersInit {
  if (!STATE_CHANGING_METHODS.has((method || 'GET').toUpperCase())) return headers;
  const token = getCsrfToken();
  if (!token) return headers;
  const h = new Headers(headers);
  h.set('X-CSRF-Token', token);
  return h;
}

/** Human-readable message shown when CSRF recovery fails and re-login is needed. */
export const CSRF_RELOGIN_MESSAGE =
  'Your session security check failed. Please sign in again.';

/**
 * Detect the CSRF middleware's 403 rejection without consuming the
 * response body (uses a clone so callers can still read it).
 *
 * IMPORTANT: matches ONLY the explicit `code: 'csrf_token_mismatch'` the
 * CSRF middleware emits. Ordinary permission 403s (e.g. forbidden_project)
 * must pass through untouched — treating them as CSRF failures would log
 * users out just for lacking access to a resource.
 */
export async function isCsrfRejection(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = await res.clone().json();
    return body?.code === 'csrf_token_mismatch';
  } catch {
    return false;
  }
}

/**
 * A retry can only safely re-send bodies that fetch re-serializes each
 * time (strings, FormData, Blob, URLSearchParams, undefined). Stream
 * bodies are consumed by the first attempt and would throw on reuse.
 */
function isRetriableBody(body: BodyInit | null | undefined): boolean {
  return !(typeof ReadableStream !== 'undefined' && body instanceof ReadableStream);
}

export async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const doFetch = () =>
    fetch(url, {
      ...init,
      headers: withCsrfHeader(init.method, init.headers),
      credentials: 'include',
    });

  let res = await doFetch();

  // Edge case: csrf-token cookie expired/cleared while auth cookies survive.
  // /api/auth/refresh is CSRF-exempt and re-issues the csrf cookie, so try
  // one silent refresh + retry before surfacing an error.
  if ((await isCsrfRejection(res)) && isRetriableBody(init.body)) {
    const { refreshAuthToken } = await import('./auth');
    if (await refreshAuthToken()) {
      res = await doFetch(); // withCsrfHeader re-reads the fresh cookie
    }
  }

  return res;
}
